import React, { useState, useEffect } from "react";
import Icon from "../../common/elements/Icon";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {instrumentationPatterns, instrumentationSources, pagesUnderTest, thor_api_urls} from "../../common/Constants";
import Select from "react-select";
import Button from "../../common/elements/Button";
import useFetchWithMsal from "../../../useFetchWithMsal";

const UpdateInstrumentationInstructionsModal = (props) => {
    const {
        isActive = false,
        onClose,
        instrumentationInstructions = {},
        experimentKey,
        onSuccess,
    } = props;

    const { execute } = useFetchWithMsal();
    
    // State management for form fields
    const [formData, setFormData] = useState({
        instrumentationPattern: null,
        pagesUnderTest: [],
        expectedTrafficExposure: '',
        instrumentationSources: [],
        impactedServices: [],
        customEligibilityChecks: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Initialize form data when modal opens or instrumentationInstructions change
    useEffect(() => {
        if (isActive && instrumentationInstructions) {
            setFormData({
                instrumentationPattern: instrumentationInstructions.instrumentationPattern 
                    ? instrumentationPatterns.find(pattern => pattern.value === instrumentationInstructions.instrumentationPattern)
                    : null,
                pagesUnderTest: convertFromStringArrayToSelectFormat(instrumentationInstructions.pagesUnderTest, pagesUnderTest),
                expectedTrafficExposure: instrumentationInstructions.expectedTrafficExposure || '',
                instrumentationSources: convertFromStringArrayToSelectFormat(instrumentationInstructions.instrumentationSources, instrumentationSources),
                impactedServices: convertFromStringArrayToSelectFormat(instrumentationInstructions.impactedServices, instrumentationSources),
                customEligibilityChecks: instrumentationInstructions.customEligibilityChecks || ''
            });
            setError(null);
        }
    }, [isActive, instrumentationInstructions]);

    const convertFromStringArrayToSelectFormat = (input, options) => {
        if (!input || !Array.isArray(input)) {
            return [];
        }
        const validItems = input.filter((item) => item != null && item !== '');
        return validItems.map((item) => {
            const matchingOption = options.find((option) => option.value === item);
            return matchingOption ?? { value: item, label: item };
        });
    };

    // Form field change handlers
    const handleInstrumentationPatternChange = (selectedOption) => {
        setFormData(prev => ({ ...prev, instrumentationPattern: selectedOption }));
    };

    const handlePagesUnderTestChange = (selectedOptions) => {
        setFormData(prev => ({ ...prev, pagesUnderTest: selectedOptions || [] }));
    };

    const handleExpectedTrafficExposureChange = (event) => {
        setFormData(prev => ({ ...prev, expectedTrafficExposure: event.target.value }));
    };

    const handleInstrumentationSourcesChange = (selectedOptions) => {
        setFormData(prev => ({ ...prev, instrumentationSources: selectedOptions || [] }));
    };

    const handleImpactedServicesChange = (selectedOptions) => {
        setFormData(prev => ({ ...prev, impactedServices: selectedOptions || [] }));
    };

    const handleCustomEligibilityChecksChange = (event) => {
        setFormData(prev => ({ ...prev, customEligibilityChecks: event.target.value }));
    };

    // API call to update instrumentation instructions
    const handleSubmit = async () => {
        if (!experimentKey) {
            setError('Experiment key is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            // This will skip undefined, null and empty string
            const toValues = (arr) => (arr || []).map((item) => item?.value ?? item).filter((v) => v != null && v !== '');

            // Prepare data for API call
            const updateData = {
                instrumentationPattern: formData.instrumentationPattern?.value || null,
                pagesUnderTest: toValues(formData.pagesUnderTest),
                expectedTrafficExposure: formData.expectedTrafficExposure,
                instrumentationSources: toValues(formData.instrumentationSources),
                impactedServices: toValues(formData.impactedServices),
                customEligibilityChecks: formData.customEligibilityChecks
            };

            const endpoint = thor_api_urls.instrumentationInstructions(experimentKey);
            await execute('POST', endpoint, updateData);
            
            // Success callback
            if (onSuccess) {
                onSuccess();
            }
            
            // Close modal
            onClose();
            
        } catch (error) {
            console.error('Failed to update instrumentation instructions:', error);
            const message = error?.message || error?.status
                ? `Failed to update: ${error.status ? `(${error.status}) ` : ''}${error.message || ''}`
                : 'Failed to update instrumentation instructions. Please try again.';
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isActive) {
        return null;
    }

    return (
        <div className="modal is-active">
            <div className="modal-background"></div>
            <div className="modal-card" style={{width: '90%', maxWidth: '800px'}}>
                <header className="modal-card-head">
                    <p className="modal-card-title">Update Instrumentation Instructions</p>
                    <button
                        className="delete"
                        aria-label="close"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        <Icon icon={faTimes} />
                    </button>
                </header>
                <div className="modal-card-body">
                    {error && (
                        <div className="notification is-danger">
                            <button className="delete" onClick={() => setError(null)}></button>
                            {error}
                        </div>
                    )}
                    
                    <div className="mt-4">
                        <label className="label">Instrumentation Pattern:</label>
                        <Select
                            options={instrumentationPatterns}
                            value={formData.instrumentationPattern}
                            onChange={handleInstrumentationPatternChange}
                            placeholder="Select Instrumentation Pattern"
                            className="basic-select"
                            classNamePrefix="select"
                            isDisabled={isSubmitting}
                        />
                    </div>
                    
                    <div className="mt-4">
                        <label className="label">Pages Under Test:</label>
                        <Select
                            isMulti
                            options={pagesUnderTest}
                            value={formData.pagesUnderTest}
                            onChange={handlePagesUnderTestChange}
                            placeholder="Select Pages Under Test"
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isDisabled={true}
                        />
                    </div>
                    
                    <div className="mt-4">
                        <label className="label">Percentage of traffic exposed on Impacted Pages:</label>
                        <input
                            className="input"
                            type="text"
                            placeholder="Enter the exposed traffic percentage..."
                            title="This field is read-only"
                            value={formData.expectedTrafficExposure}
                            onChange={handleExpectedTrafficExposureChange}
                            disabled={true}
                            style={{width: '100%'}}
                        />
                    </div>
                    
                    <div className="mt-4">
                        <label className="label">Instrumentation Sources:</label>
                        <Select
                            isMulti
                            options={instrumentationSources}
                            value={formData.instrumentationSources}
                            onChange={handleInstrumentationSourcesChange}
                            placeholder="Select Instrumentation Sources"
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isDisabled={isSubmitting}
                        />
                    </div>
                    
                    <div className="mt-4">
                        <label className="label">Impacted Services:</label>
                        <Select
                            isMulti
                            options={instrumentationSources}
                            value={formData.impactedServices}
                            onChange={handleImpactedServicesChange}
                            placeholder="Select Impacted Services"
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isDisabled={isSubmitting}
                        />
                    </div>
                    
                    <div className="mt-4">
                        <label className="label">Audience (Custom Eligibility Checks)</label>
                        <input
                            className="input"
                            type="text"
                            placeholder="Enter the value..."
                            value={formData.customEligibilityChecks}
                            onChange={handleCustomEligibilityChecksChange}
                            disabled={true}
                            style={{width: '100%'}}
                        />
                    </div>
                </div>
                
                <footer className="modal-card-foot" style={{flexDirection: 'column', alignItems: 'stretch'}}>
                    <div style={{display: 'flex', justifyContent: 'center'}}>
                        <Button
                            className="button is-primary"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Updating...' : 'Submit'}
                        </Button>
                    </div>
                </footer>
            </div>
        </div>
    );
};
export default UpdateInstrumentationInstructionsModal